<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap payment-page">
        <h1 class="capitalize-text">{{ $tc('payments.payments', 1) }}</h1>
        <template v-if="orderEmpty">
            <div class="row items-end q-col-gutter-lg">
                <p class="q-mb-lg text-subtitle1">
                    {{ $t('payments.error.unable_to_obtain_order_data') }}
                </p>
            </div>
        </template>
        <template v-else>
            <div class="row items-end q-col-gutter-lg">
                <!-- order data -->
                <div class="col-12 col-md-12">
                    <p class="q-mb-lg text-subtitle1">
                        {{
                            $t('payments.order_details', { orderId: order.id })
                        }}
                    </p>
                    <!-- order details -->
                    <div class="row">
                        <!-- date -->
                        <div class="col-4">
                            <h6 class="text-capitalize">
                                {{ $t('general.date') }}
                            </h6>
                            <h4>{{ formatDate(order.order_date) }}</h4>
                        </div>
                        <!-- status -->
                        <div class="col-4">
                            <h6 class="text-capitalize">
                                {{ $t('general.status') }}
                            </h6>
                            <h4>
                                {{
                                    JSONParseTranslations(
                                        order.order_status,
                                        locale.key
                                    )
                                }}
                            </h4>
                        </div>
                        <!-- total -->
                        <div class="col-4">
                            <h6 class="text-capitalize">
                                {{ $t('general.total') }}
                            </h6>
                            <h4>
                                {{
                                    new Intl.NumberFormat(
                                        currency.definition['locale'],
                                        {
                                            style: 'currency',
                                            currency: currency.key,
                                        }
                                    ).format(
                                        JSON.parse(order.total)[currency.key]
                                    )
                                }}
                            </h4>
                        </div>
                    </div>
                    <!-- separator -->
                    <q-separator class="q-my-md" />
                    <!-- payment details -->
                    <div class="col-12 col-md-12 q-mt-xl">
                        <p class="q-mb-lg text-subtitle1">
                            {{ $t('payments.order_items_in_payment') }}
                        </p>
                    </div>
                    <!-- order items -->
                    <div class="row q-mt-lg">
                        <template
                            v-if="
                                invoice &&
                                invoice.documents &&
                                invoice.documents.orderItems
                            "
                        >
                            <div class="col-12 col-md-9">
                                <OrderItem
                                    v-for="(orderItem, index) in invoice
                                        .documents.orderItems"
                                    :key="index"
                                    class="col-12"
                                    :index="index"
                                    :order-item="orderItem"
                                ></OrderItem>
                            </div>
                        </template>
                        <template
                            v-if="
                                invoice &&
                                invoice.documents &&
                                invoice.documents.orderServices
                            "
                        >
                            <div class="col-12 col-md-9">
                                <OrderService
                                    v-for="(orderService, index) in invoice
                                        .documents.orderServices"
                                    :key="index"
                                    class="col-12"
                                    :index="index"
                                    :order-service="orderService"
                                ></OrderService>
                            </div>
                        </template>
                        <!-- invoice total -->
                        <div class="col-12 col-md-3">
                            <div class="cart-total">
                                <h6>{{ $t('general.total') }}:</h6>
                                <strong>
                                    {{
                                        new Intl.NumberFormat(
                                            currency.definition['locale'],
                                            {
                                                style: 'currency',
                                                currency: currency.key,
                                            }
                                        ).format(
                                            JSON.parse(invoice.total)[
                                                currency.key
                                            ]
                                        )
                                    }}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- selected payment method details -->
                <div class="col-12 col-md-12 q-mt-lg">
                    <p class="q-mb-md text-subtitle1">
                        {{ $t('payments.selected_payment_method') }}
                    </p>
                    <!-- loading payment methods -->
                    <template v-if="isFetchingPaymentMethods"></template>
                    <!-- payment methods loaded -->
                    <template v-else>
                        <div
                            v-if="paymentMethods.length"
                            class="row items-center"
                        >
                            <!-- render radio buttons for available payment methods -->
                            <div class="column col-12">
                                <q-radio
                                    v-for="(method, index) in paymentMethods"
                                    :key="index"
                                    v-model="selectedPaymentMethod"
                                    :val="method.id"
                                    size="xl"
                                >
                                    {{
                                        JSONParseTranslations(
                                            method.title,
                                            locale.key
                                        )
                                    }}
                                </q-radio>
                            </div>
                            <!-- payment method description -->
                            <h4
                                class="q-mt-md"
                                v-html="paymentMethodDescription"
                            ></h4>
                        </div>
                    </template>
                </div>
            </div>
            <!-- buttons -->
            <div class="row items-end q-col-gutter-lg q-mt-md justify-center">
                <div class="col-12 col-md-4 col-xl-3">
                    <q-btn
                        class="sign-up-btn full-width"
                        color="secondary"
                        size="lg"
                        :label="$t('actions.confirm')"
                        @click="savePayment"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import paymentMethodsService from '@/services/modules/paymentMethods'
import paymentStatusService from '@/services/modules/paymentstatus'
import paymentsService from '@/services/modules/payments'
import invoicesService from '@/services/modules/invoices'
import translate from '@/local-shared/mixins/translate'
import OrderItem from '@/components/payment/OrderItem'
import OrderService from '@/components/payment/OrderService'
import ordersService from '@/services/modules/orders'
import PageLoading from '@/components/PageLoading'
import moment from 'moment'
import store from '@/store'
import _ from 'lodash'

const loadingKeys = {
    fetchingOrders: 'fetching-orders',
    fetchingPaymentMethods: 'fetching-payment-methods',
    savingPayment: 'saving-payment',
    fetchingPaymentStatus: 'fetching-payment-status',
    fetchingInvoices: 'fetching-invoices',
}

export default {
    name: 'PaymentView',

    components: {
        PageLoading,
        OrderItem,
        OrderService,
    },

    mixins: [translate],

    data() {
        return {
            paymentStatus: {},
            paymentMethods: [],
            order: {},
            invoice: {},
            selectedPaymentMethod: null,
        }
    },

    computed: {
        user() {
            return store.getters['session/user']
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        isLoading() {
            return this.isFetchingOrders
        },

        isFetchingPaymentMethods() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPaymentMethods
            ).length
        },

        isFetchingPaymentStatus() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPaymentStatus
            ).length
        },

        isFetchingOrders() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingOrders
            ).length
        },

        isFetchingInvoices() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingInvoices
            ).length
        },

        isSavingPayment() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.savingPayment
            ).length
        },

        orderEmpty() {
            return _.isEmpty(this.order)
        },

        paymentMethodDescription() {
            let paymentMethod = this.paymentMethods.filter(
                (paymentMethod) =>
                    paymentMethod.id === this.selectedPaymentMethod
            )
            if (paymentMethod.length) {
                return this.JSONParseTranslations(
                    paymentMethod[0].description,
                    this.locale.key
                )
            } else {
                return ''
            }
        },
    },

    async created() {
        this.fetchPaymentMethods()
        this.fetchPaymentStatus()
        this.fetchInvoices()
        await this.fetchOrders()
    },

    methods: {
        async fetchPaymentStatus() {
            this.loadingAdd(loadingKeys.fetchingPaymentStatus)

            let parameters = {
                filter: {
                    slug: 'pending',
                },
            }

            let response = await paymentStatusService.retrieve(parameters)

            if (response.status === 200) {
                this.paymentStatus = response.data.results[0]
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingPaymentStatus)
        },

        async fetchPaymentMethods() {
            this.loadingAdd(loadingKeys.fetchingPaymentMethods)

            let parameters = {
                filter: {
                    application: 'frontend',
                    enabled: true,
                },
            }

            let response = await paymentMethodsService.retrieve(parameters)

            if (response.status === 200) {
                this.paymentMethods = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingPaymentMethods)
        },

        async fetchInvoices() {
            this.loadingAdd(loadingKeys.fetchingInvoices)

            let parameters = {
                filter: {
                    id: this.$route.params.invoiceId,
                },

                relations: {
                    currency: true,
                    payments: true,
                    documents: {
                        withoutContent: true,
                        orderItems: true,
                        orderServices: true,
                    },
                },
            }

            let response = await invoicesService.retrieve(parameters)

            if (response.status === 200) {
                this.invoice = response.data.results[0]
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingInvoices)
        },

        async fetchOrders() {
            this.loadingAdd(loadingKeys.fetchingOrders)

            let parameters = {
                filter: {
                    id: this.$route.params.orderId,
                },
                relations: {
                    orderItems: true,
                    currencies: true,
                    paymentMethod: true,
                    orderStatus: true,
                    orderServices: true,
                },
            }

            let response = await ordersService.retrieve(parameters)

            if (response.status === 200) {
                this.order = response.data.results[0]
                this.selectedPaymentMethod =
                    response.data.results[0].payment_method_id
            } else {
                this.notifyError(response.data.message)
            }

            this.loadingRemove(loadingKeys.fetchingOrders)
        },

        dateMaskFormat() {
            return this.locale &&
                this.locale.definition &&
                this.locale.definition.dateFormat
                ? this.locale.definition.dateFormat
                : 'DD/MM/YYYY'
        },

        formatDate(date) {
            return moment(date).format(this.dateMaskFormat() + ' HH:mm')
        },

        async savePayment() {
            this.loadingAdd(loadingKeys.savingPayment)

            let parameters = {
                data: [
                    {
                        payment_method_id: this.selectedPaymentMethod,
                        payment_status_id: this.paymentStatus.id,
                        invoice_id: this.$route.params.invoiceId,
                        payment_date: null,
                        expire_date: null,
                        transaction_id: null,
                        details: JSON.stringify({
                            amount: JSON.parse(this.invoice.total),
                        }),
                    },
                ],
            }

            let response = await paymentsService.create(parameters)

            if (response.status === 200) {
                this.$q.notify({
                    type: 'positive',
                    message: this.$t(
                        'notify.payment_correctly_registered_in_the_system'
                    ),
                })
                this.$router.push({
                    name: 'SingleOrder',
                    params: {
                        orderId: this.order.id,
                    },
                })
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.savingPayment)
        },
    },
}
</script>
